/**
 * The audio uplink's lifecycle, owned by the engine rather than by a screen.
 *
 * This is the serving path. Since PR #242 and the C7.5 chain retired the browser
 * upload routes and the worker's batch fallback (`docs/DEPLOY-RAILWAY.md` §6.3), the
 * ticketed WebSocket in `audio-uplink.ts` is how a recording's audio reaches the
 * server -- there is no second lane to fall back to and nothing left to compare
 * against. A failure here is lost audio, not a lost measurement.
 *
 * [V] This used to live in `routes/diagnostics.tsx`, where an unmount effect stopped
 * the socket. So the stream lasted exactly as long as somebody stayed on the debug
 * page, and the measured cost was real: the first realtime-vs-batch parity run
 * (#106) came back with coverage 0.9428 because navigating away mid-session cut the
 * device's own contribution out of the merged timeline. A stream that dies when a
 * route unmounts is not a stream, it is a side effect of looking at a page.
 *
 * Nothing about the socket protocol lives here -- that is `audio-uplink.ts`. What this
 * module decides is who holds the reference and when it ends: the engine, for as long
 * as the recording lasts.
 *
 * ## The API is shaped for the pipeline and view gates (#116 slice 2)
 *
 * `start`/`stop` are plain lifecycle calls with no opinion about *why* they were
 * called. `startStreamIfEnabled` is driven by the recording lifecycle (`session.ts`)
 * and `stopStreamForRecordingEnd` by the engine's own wrap-up; the capability gates
 * live with the caller, and this module never reads the URL. The diagnostics panel
 * behind `?stream-probe=1` observes the same uplink rather than owning it.
 *
 * ## Bounded client recovery
 *
 * The archive resume contract allows the browser stream to recover from edge-generated
 * drops (such as 1011 keepalive ping timeouts or transient network interruptions)
 * while recording remains active. Recovery is bounded: up to 2 reconnect attempts per
 * recording with exponential backoff (1s then 4s). On reconnect, a fresh single-use
 * ticket is minted, a new transport is created with the same pinned `timelineEpoch`,
 * and audio is re-fed from sample 0 using the existing backlog drain path. The gateway
 * acknowledges replayed frames with `ack` `status: "consumed"`, which is treated as
 * progress. Refusals (1008 `archive_epoch_conflict`, `resume_cursor_required`,
 * `archive_resume_boundary_unavailable`) and clean client stops (1000/1001) fail closed
 * and never reconnect.
 *
 * A *second* lane covers the case where the server said it was going away on purpose
 * (1012/1013): 3 attempts over a 3-minute window, first at ~30 s with jitter, and an
 * `abnormal` close inside that window joins it rather than spending the 2-attempt budget
 * above. See `MAX_STREAM_RESTART_RECONNECT_ATTEMPTS` for why (#264 S2).
 *
 * ## After the budget: keep trying, then upload the backlog (#356)
 *
 * [V] Both lanes used to fail closed when their budget ran out, and so did a start that
 * failed or hit the readiness deadline. That was right while the stream only fed a live
 * view. The gateway now opens no provider and only archives, so a device's audio is owed
 * to the server whether or not anyone is watching live. So a spent budget falls through
 * to a slow retry (`STREAM_SLOW_RETRY_MS`) for as long as the recording lasts, and at
 * Stop `uploadBacklogAfterStop` reopens a connection if the device still owes audio,
 * inside `STREAM_BACKLOG_UPLOAD_WINDOW_MS`. Each connection probes with one frame and
 * jumps to the server's durable cursor, so a reconnect re-sends only what the server
 * lacks rather than "re-feeding from sample 0" as described above. Typed refusals still
 * end the lane: they would only repeat.
 *
 * ## A resumable close is not a refusal (#410)
 *
 * [V] The gateway closes each connection at a per-connection cap (`stream_byte_limit`,
 * `stream_duration_limit`) and on a few other conditions it recovers from
 * (`missing_tail`, `frame_processing_timeout`, `stream_replay_limit`), all with 1008 --
 * the refusal code -- and keeps the resume cursor. This module read every 1008 as a
 * refusal, set `laneRefused`, never reconnected and skipped the upload after Stop, so
 * every meeting over 15 minutes lost each device's audio from minute 15 on. `classifyClose`
 * now says `resumable` for those reasons (one list, shared with the gateway), and the
 * resumable lane below reconnects at once and resumes from the durable boundary. It has
 * its own backoff, which escalates only while connections close without progress, and it
 * never runs out and never touches the other lanes' budgets.
 */

/**
 * Resumable-lane backoff (#410): how long to wait before reconnecting after the Nth
 * resumable close in a row with no accepted frame in between.
 *
 * [D] The first wait is short because the ordinary case is the per-connection cap, where
 * the server is healthy and waiting for the device to come back. Later waits grow so a
 * condition that recurs at once (a slow archive answering `frame_processing_timeout` on
 * every connection) does not become a reconnect loop. Past the end of the list, the last
 * value repeats; the lane never gives up while the recording lasts.
 */
export const STREAM_RESUMABLE_RECONNECT_BACKOFF_MS = [250, 1_000, 4_000, 10_000, 30_000] as const;

import * as K from "../../lib/const.js";
import * as telemetry from "../../lib/telemetry.js";
import {
  classifyClose,
  recorderCapture,
  AudioUplink,
  STREAM_FRAME_SAMPLES,
  type CloseKind,
  type AudioUplinkCapture,
  type AudioUplinkHealthSnapshot,
  type AudioUplinkSocketDiagnostics,
} from "./audio-uplink";
import {
  AudioUplinkTelemetry,
  type StreamProbeReconnectLane,
  type AudioUplinkStopReason,
} from "./audio-uplink-telemetry";
import { useApp } from "../state/store";
import { isAudioNotAccepted } from "./server-config";

/** Maximum number of reconnect attempts allowed per recording before failing closed. */
export const MAX_STREAM_RECONNECT_ATTEMPTS = 2;
/** Exponential backoff delays in milliseconds for reconnect attempts (1s then 4s). */
export const STREAM_RECONNECT_BACKOFF_MS = [1_000, 4_000] as const;

/**
 * ## The second reconnect lane: a deploy is not a network blip (#264 S2)
 *
 * [V] 2026-09-02, 06:02 UTC, and what the evidence shows is a *shape*, not a duration:
 * a Railway redeploy cut every in-flight stream with 1012, the browser spent both of its
 * abnormal-lane attempts -- 1 s and 4 s -- against a container that was still booting,
 * failed closed five seconds in, and told the user the live view had "ended"; the
 * replacement replica then killed the first stream it admitted with 1011
 * `provider_error` on a cold provider connection. The outage's length was not measured,
 * so no figure for it is claimed here. The api service has `drainingSeconds` unset
 * (measured from the live service manifest, not assumed), so there is no drain: the old
 * container is replaced, and the correct client behaviour is to wait for the new one
 * rather than to race it.
 *
 * [D] What the constants below cover, as arithmetic rather than as an observation: the
 * first restart attempt lands at 30-40 s, so a replacement that is serving by then is
 * caught by attempt 1; attempts 2 and 3 extend the reach to roughly 150 s, inside the
 * 180 s window. A restart slower than that fails closed with a message that says so.
 *
 * So a `service_restart` close opens a window and reconnects on a much longer clock, and
 * an `abnormal` close *while that window is open* joins the same lane -- because at
 * 06:02 the first stream the replacement replica admitted failed with 1011
 * `provider_error` on a cold provider connection, and treating that as an unrelated
 * network drop throws away the patience this lane exists to provide. That is one
 * observation, not a measured rate; the lane is right either way, since the cost of
 * adopting an ordinary drop inside the window is at most a longer wait, while the cost
 * of not adopting a restart-caused one is ending the live view mid-meeting.
 *
 * The two lanes keep separate budgets. Restart-lane attempts never consume
 * `reconnectAttempts`, so a deploy cannot leave a recording with no recovery left for
 * the ordinary drop that follows it once the window has expired.
 */
export const MAX_STREAM_RESTART_RECONNECT_ATTEMPTS = 3;
/**
 * Restart-lane backoff: 30 s, then 60 s, then 60 s.
 *
 * [D] The first wait is 30 s because a Railway replacement is container start plus health
 * check, not a retransmit -- anything shorter is a request aimed at a port that is not
 * listening yet. The three together bound the lane at ~2.5 min of backoff inside the
 * 3-minute window, so the budget expires by exhaustion rather than by the window closing
 * underneath a pending timer.
 */
export const STREAM_RESTART_RECONNECT_BACKOFF_MS = [30_000, 60_000, 60_000] as const;
/**
 * Extra uniform jitter added to the *first* restart-lane attempt only.
 *
 * [D] Without it this lane recreates #264's root cause at the handshake. Every device in
 * a session sees the 1012 within about a second of the others, so a fixed 30 s backoff
 * re-admits all of them simultaneously and they queue on `accept_stream_connection`'s
 * `Session FOR UPDATE` -- single-session row-lock contention, which is the whole of this
 * issue. Spreading attempt 1 over 30-40 s de-synchronises the fleet permanently: each
 * later attempt is 60 s after that device's own previous one, so the spread persists and
 * jittering attempts 2 and 3 as well would buy nothing.
 */
export const STREAM_RESTART_RECONNECT_JITTER_MS = 10_000;
/**
 * How long an `abnormal` close still counts as fallout from the restart.
 *
 * [D] 3 minutes, and the message the user gets on exhaustion quotes it, so the two must
 * not drift apart. Wide enough to cover a container replacement plus the cold-start 1011
 * that follows it; short enough that a recording which merely *happened* to see a deploy
 * an hour ago is back on the ordinary 2-attempt policy long before its next real drop.
 */
export const STREAM_RESTART_WINDOW_MS = 180_000;

/** A first turn that arrived before `start()` returned, so it can be timed once it has. */
interface PendingFirstTurn {
  turnOrder: number;
  atMs: number;
}

/**
 * The mint bucket: connections that may reach the server back to back (#943).
 *
 * [D] The server allows 30 ticket mints per device per hour and counts the refused ones
 * (`meetfolks/server/limits.py`, `STREAM_TICKET_MINT_PER_DEVICE`). The fast lanes and the
 * `online` listener can each ask for a fresh ticket within seconds of the last one, so
 * this module holds the same hard bound the Rust core does
 * (`core/crates/mf-engine/src/uplink.rs`): four tokens, one back every
 * `STREAM_MINT_REFILL_MS`. Before Stop a connection leaves the last token for the drain
 * (`MINT_RESERVE_FOR_DRAIN`), so an outage cannot spend the ticket the backlog upload
 * needs.
 */
export const STREAM_MINT_BUCKET = 4;
/**
 * How often the bucket gets one token back, matching the shipped core.
 *
 * [D] 160 s. Refills land at least this far apart, so any hour holds at most
 * `3600 / 160 + 1 = 23` of them: with the full bucket and the recording's own first mint
 * that is `4 + 23 + 1 = 28`, under the server's 30.
 */
export const STREAM_MINT_REFILL_MS = 160_000;
/**
 * The least wait before a mint once the server has refused to settle
 * `MAX_UNPRODUCTIVE_MINTS` connections in a row: the core's `STREAM_MINT_SPACING_MS`.
 */
export const STREAM_MINT_SPACING_MS = 150_000;
/**
 * Server-reaching mints in a row, without a connection settling, before the spacing above
 * applies. A 429 is the limit itself and jumps straight to that spacing.
 */
export const MAX_UNPRODUCTIVE_MINTS = 3;
/**
 * The tokens a pre-Stop reconnect must leave in the bucket: one for it, one for the Stop
 * drain. A connection during the drain needs only one.
 */
const MINT_RESERVE_FOR_DRAIN = 2;
/**
 * How often a reconnect retries while the device has no network. Such a mint is never
 * issued, so it costs the bucket and the server nothing; this is the core's
 * `STREAM_OFFLINE_RETRY_MS`.
 */
export const STREAM_OFFLINE_RETRY_MS = 5_000;

/**
 * What a pending reconnect is waiting on. It decides whether the network's return may cut
 * the wait short (#943): a lane, the slow retry and the offline retry go at once; a server
 * restart's wait, the mint spacing and a wait for a mint token do not.
 */
type ReconnectWaitKind =
  | "lane"
  | "abnormal"
  | "slow"
  | "resumable"
  | "restart"
  | "spacing"
  | "token";

/**
 * Everything the stream owns, beside the engine's own recorder and outbox.
 *
 * Module-level rather than per-session because there is one microphone and one
 * socket. `starting` is separate from `uplink` for the reason it always was: a
 * second `start()` while the first is still opening must not build a second
 * socket, and `uplink` alone cannot tell "opening" from "open".
 */
const stream: {
  uplink: AudioUplink | null;
  starting: AudioUplink | null;
  /**
   * The uplink `stopStream` asked to close, held for `awaitStreamClosed`.
   *
   * Needed because `stopStream` clears `uplink` *before* awaiting the stop, so
   * that `onClosed` cannot mistake a deliberate teardown for a socket that died
   * on its own. Without this stash `awaitStreamClosed` read a reference that was
   * already `null` and returned at once on every real stop: the socket was never
   * settled before teardown, and `stream_gateway_closed` -- the one device row
   * that proves what L1 saved -- was never logged (#272 R1).
   */
  closingUplink: AudioUplink | null;
  recorder: Pick<AudioUplinkCapture, "sampleCount" | "seconds" | "sliceSamples" | "slice"> | null;
  timelineEpoch: string | null;
  anchorReferenceDeviceId: string | null;
  pendingFirstTurn: PendingFirstTurn | null;
  telemetry: AudioUplinkTelemetry;
  reconnectAttempts: number;
  /**
   * `performance.now()` past which a restart is no longer an explanation for a drop.
   * Zero means no restart has been seen this recording, which every comparison below
   * reads correctly as a closed window.
   */
  restartWindowUntilMs: number;
  restartReconnectAttempts: number;
  /**
   * Resumable closes in a row with no accepted frame since (#410). Reset by every
   * `accepted` ack, so a device crossing the per-connection cap each few hours is always
   * on the first, shortest wait.
   */
  resumableReconnectAttempts: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  clientStopRequested: boolean;
  mode: "auto" | "manual";
  recordingStartedAtMs: number | null;
  /**
   * When `stopStream` asked the gateway to close. `stream_gateway_closed`
   * measures its `dur` from here rather than from the moment anyone got round
   * to awaiting it -- which is the whole point of #272 L1, and would read as
   * ~0 ms if measured from the await.
   */
  stopRequestedAtMs: number | null;
  /** A typed 1008 refusal ended this recording's lane; nothing retries after it. */
  laneRefused: boolean;
  /** The last uplink attempt number, so a slow retry keeps the sequence monotonic. */
  lastAttempt: number;
  /** The post-stop backlog upload, if one is running; `awaitStreamClosed` waits for it. */
  backlogUpload: Promise<void> | null;
  /**
   * Settles once the server has this recording's audio, or the upload window has ended
   * (#894). Earlier than `backlogUpload`, which also waits for the gateway's close;
   * `awaitAudioDelivered` waits for this one.
   */
  backlogDelivered: Promise<void> | null;
  /**
   * The mint bucket (#943): connections that may reach the server before a refill. Starts
   * full; the recording's own first connection is free of it, matching the core's
   * host-minted first ticket, and every reconnect and drain attempt spends one.
   */
  mintTokens: number;
  /** The one-token-at-a-time refill timer, or `null` when the bucket is full. */
  mintRefillTimer: ReturnType<typeof setTimeout> | null;
  /** When `mintRefillTimer` fires, so a token wait can tell how long it is. */
  mintRefillDueAtMs: number | null;
  /** Server-reaching mints in a row with no connection settling since the last one. */
  unproductiveMints: number;
  /** Whether this recording's free first connection has been opened already. */
  hostMintSpent: boolean;
  /** The browser's network went and has not returned. */
  networkLost: boolean;
  /** What `reconnectTimer` waits on, or `null` when none is pending. */
  reconnectWaitKind: ReconnectWaitKind | null;
} = {
  uplink: null,
  starting: null,
  closingUplink: null,
  recorder: null,
  timelineEpoch: null,
  anchorReferenceDeviceId: null,
  pendingFirstTurn: null,
  telemetry: new AudioUplinkTelemetry((event, detail, durMs) =>
    telemetry.log(event, detail, durMs),
  ),
  reconnectAttempts: 0,
  restartWindowUntilMs: 0,
  restartReconnectAttempts: 0,
  resumableReconnectAttempts: 0,
  reconnectTimer: null,
  clientStopRequested: false,
  mode: "auto",
  recordingStartedAtMs: null,
  stopRequestedAtMs: null,
  laneRefused: false,
  lastAttempt: 1,
  backlogUpload: null,
  backlogDelivered: null,
  mintTokens: STREAM_MINT_BUCKET,
  mintRefillTimer: null,
  mintRefillDueAtMs: null,
  unproductiveMints: 0,
  hostMintSpent: false,
  networkLost: false,
  reconnectWaitKind: null,
};

function clearReconnectTimer(): void {
  if (stream.reconnectTimer !== null) {
    clearTimeout(stream.reconnectTimer);
    stream.reconnectTimer = null;
  }
  stream.reconnectWaitKind = null;
}

/**
 * Arm the one pending reconnect timer, and remember what it waits on.
 *
 * One place sets both, so the `online` listener can never read a kind that does not belong
 * to the timer it is looking at.
 */
function armReconnectTimer(kind: ReconnectWaitKind, delayMs: number, onFire: () => void): void {
  clearReconnectTimer();
  stream.reconnectWaitKind = kind;
  stream.reconnectTimer = setTimeout(() => {
    stream.reconnectTimer = null;
    stream.reconnectWaitKind = null;
    onFire();
  }, Math.max(0, delayMs));
}

/**
 * A lane's wait, held to the mint spacing once mints stop settling (#943).
 *
 * The core applies this at reconnect-scheduling time (`schedule_reconnect`), and so does
 * the web: a wait shorter than `STREAM_MINT_SPACING_MS` becomes one, and reports as the
 * `spacing` wait so the network's return never cuts it short.
 */
function spacedWait(
  lane: ReconnectWaitKind,
  laneMs: number,
): { lane: ReconnectWaitKind; ms: number } {
  if (stream.unproductiveMints < MAX_UNPRODUCTIVE_MINTS) return { lane, ms: laneMs };
  if (laneMs >= STREAM_MINT_SPACING_MS) return { lane, ms: laneMs };
  return { lane: "spacing", ms: STREAM_MINT_SPACING_MS };
}

function clearMintRefillTimer(): void {
  if (stream.mintRefillTimer !== null) {
    clearTimeout(stream.mintRefillTimer);
    stream.mintRefillTimer = null;
  }
  stream.mintRefillDueAtMs = null;
}

/**
 * A token back in time, while the bucket is short. One at a time, like the core's
 * `arm_refill`, so an hour can never hold more refills than `STREAM_MINT_REFILL_MS` allows.
 */
function armMintRefill(): void {
  if (stream.mintTokens >= STREAM_MINT_BUCKET || stream.mintRefillTimer !== null) return;
  if (stream.mintRefillDueAtMs === null) {
    stream.mintRefillDueAtMs = performance.now() + STREAM_MINT_REFILL_MS;
  }
  const dueAtMs = stream.mintRefillDueAtMs;
  stream.mintRefillTimer = setTimeout(() => {
    stream.mintRefillTimer = null;
    stream.mintRefillDueAtMs = null;
    stream.mintTokens = Math.min(STREAM_MINT_BUCKET, stream.mintTokens + 1);
    armMintRefill();
  }, Math.max(0, dueAtMs - performance.now()));
}

/** How long until the bucket holds `needed` tokens, given the pending refills. */
function mintWaitMs(needed: number): number {
  if (stream.mintTokens >= needed) return 0;
  const shortfall = needed - stream.mintTokens;
  const now = performance.now();
  const firstAtMs = stream.mintRefillDueAtMs ?? now + STREAM_MINT_REFILL_MS;
  return Math.max(0, firstAtMs - now) + (shortfall - 1) * STREAM_MINT_REFILL_MS;
}

/**
 * Take `needed` tokens, or leave the bucket alone and say no.
 *
 * The caller waits `mintWaitMs(needed)` and tries again; nothing here opens a socket.
 */
function tryConsumeMintToken(needed: number): boolean {
  if (stream.mintTokens < needed) {
    armMintRefill();
    return false;
  }
  stream.mintTokens -= 1;
  armMintRefill();
  return true;
}

/**
 * Whether the device is offline right now, so no mint should be issued.
 *
 * Read *before* a mint, never after one. A browser that goes offline while a request is in
 * flight does not prove the request never reached the server, so the token for an issued
 * attempt always stays spent (#943). The server may have counted it.
 */
function isOfflineNow(): boolean {
  if (stream.networkLost) return true;
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Do not issue a mint while the device has no network: wait, and try the same connection
 * again. The bucket is untouched, and the `online` listener cuts this wait short.
 */
function armOfflineRetry(
  generation: number,
  sessionId: string,
  mode: "auto" | "manual",
  attempt: number,
): void {
  setUplinkState("starting");
  armReconnectTimer("lane", STREAM_OFFLINE_RETRY_MS, () => {
    if (!isCurrentRecording(generation, sessionId) || stream.clientStopRequested) return;
    void startStream(
      stream.recorder,
      mode,
      attempt,
      stream.recordingStartedAtMs ?? undefined,
      generation,
    );
  });
}

/**
 * Wait out the mint bucket, then try the same connection again.
 *
 * This is the core's `awaiting_token`: the lane has fired, but there is no ticket to be
 * had yet. The wait is its own kind, so the network's return does not jump the bucket.
 */
function armMintWait(
  generation: number,
  sessionId: string,
  mode: "auto" | "manual",
  attempt: number,
): void {
  const waitMs = mintWaitMs(MINT_RESERVE_FOR_DRAIN);
  setUplinkState("starting");
  armReconnectTimer("token", waitMs, () => {
    if (!isCurrentRecording(generation, sessionId) || stream.clientStopRequested) return;
    void startStream(
      stream.recorder,
      mode,
      attempt,
      stream.recordingStartedAtMs ?? undefined,
      generation,
    );
  });
}

/**
 * The browser's network listeners (#943).
 *
 * One pair per recording, removed when the recording ends so a finished meeting does not
 * keep a `window` handler alive into the next one. The `offline` side only records the
 * fact; the `online` side is where a waiting lane is cut short.
 */
let networkOnlineHandler: (() => void) | null = null;
let networkOfflineHandler: (() => void) | null = null;

function attachNetworkListeners(): void {
  if (networkOnlineHandler !== null) return;
  networkOnlineHandler = () => handleNetworkOnline();
  networkOfflineHandler = () => {
    stream.networkLost = true;
  };
  globalThis.addEventListener?.("online", networkOnlineHandler);
  globalThis.addEventListener?.("offline", networkOfflineHandler);
}

function detachNetworkListeners(): void {
  if (networkOnlineHandler !== null) globalThis.removeEventListener?.("online", networkOnlineHandler);
  if (networkOfflineHandler !== null) {
    globalThis.removeEventListener?.("offline", networkOfflineHandler);
  }
  networkOnlineHandler = null;
  networkOfflineHandler = null;
  stream.networkLost = false;
}

/**
 * The network is back: reconnect now, not on the wait the lane picked (#943).
 *
 * [V] After an outage the lane could be on the 30 s slow retry even though the network had
 * returned, so the backlog moved only at Stop. The core's `NetworkChanged` does this; the
 * web's `online` is the equivalent. It never jumps a server restart's wait or the mint
 * spacing, and it never opens a second socket while one is open or opening.
 */
function handleNetworkOnline(): void {
  stream.networkLost = false;
  // Any `online` is a chance to reconnect, and it is deliberately not gated on having seen
  // an `offline` (#943). The listener may have attached while the device was already
  // offline, or the `offline` event may have been missed, and `isOfflineNow` would then
  // have skipped the mint and armed the retry with `networkLost` still false. A second,
  // synchronously delivered `online` opens nothing: the first one starts the uplink before
  // it returns, so `streamUplinkActive()` is already true for the second.
  const sessionId = useApp.getState().session?.session_id ?? "";
  if (!isCurrentRecording(readiness.generation, sessionId)) return;
  if (stream.clientStopRequested || stream.laneRefused || stream.recorder === null) return;
  if (streamUplinkActive()) return;
  if (stream.reconnectTimer === null) return;
  const kind = stream.reconnectWaitKind;
  if (kind === "restart" || kind === "spacing" || kind === "token") return;
  clearReconnectTimer();
  setUplinkState("starting");
  void startStream(
    stream.recorder,
    stream.mode,
    stream.lastAttempt + 1,
    stream.recordingStartedAtMs ?? undefined,
    readiness.generation,
  );
}

/**
 * How often a recording retries once its fast reconnect budget is spent (#356).
 *
 * [D] 30 s. The fast lanes (1 s then 4 s, and the restart lane's minutes) are unchanged;
 * this is what comes after them, where the lane used to fail closed. The device keeps
 * recording locally throughout, and every reconnect resumes from the server's durable
 * cursor, so a retry that lands late costs a live view, not audio.
 */
export const STREAM_SLOW_RETRY_MS = 30_000;

/**
 * Keep trying, slowly, for as long as this recording lasts.
 *
 * No-ops for a recording that is over, a deliberate stop, a typed refusal or a missing
 * recorder -- the four cases where another socket could not help.
 */
function scheduleSlowRetry(
  generation: number,
  sessionId: string,
  mode: "auto" | "manual",
  attempt: number,
): boolean {
  if (
    !isCurrentRecording(generation, sessionId) ||
    stream.clientStopRequested ||
    stream.laneRefused ||
    stream.recorder === null
  ) {
    return false;
  }
  const wait = spacedWait("slow", STREAM_SLOW_RETRY_MS);
  armReconnectTimer(wait.lane, wait.ms, () => {
    if (!isCurrentRecording(generation, sessionId) || stream.clientStopRequested) return;
    stream.telemetry.reconnect(performance.now(), attempt + 1, {
      close_code: null,
      close_reason: null,
      timeline_epoch: stream.timelineEpoch,
      gap_ms: wait.ms,
      lane: "slow",
    });
    void startStream(
      stream.recorder,
      mode,
      attempt + 1,
      stream.recordingStartedAtMs ?? undefined,
      generation,
    );
  });
  return true;
}

/** Mark this recording's upload as refused, for the lane and for the recording screen. */
function refuseLane(): void {
  stream.laneRefused = true;
  patch({ uplinkRefused: true });
}

/* ------------------------------------------------------------------------- */
/* producer readiness latch (#170-C)                                          */
/* ------------------------------------------------------------------------- */

/**
 * How long a recording waits to learn whether it has a producer.
 *
 * [D] 15 s, and it is the *only* deadline on this question -- there is no second one in
 * the claim-delta path. Derived rather than guessed: `K.CLAIM_CHUNK_SEC` is 20, so the
 * first legacy bundle cannot exist before 20 s of elapsed audio, and deciding any time
 * inside that costs the legacy path not one bundle.
 */
export const STREAM_READINESS_DEADLINE_MS = 15_000;

/**
 * What this recording learned about its producer. One value, once, per recording.
 *
 * The names are the `claim_delta_mode` reason codes deliberately: this latch *is* the
 * answer that event reports, and translating between two vocabularies on the way out
 * would be one more place for them to drift.
 */
export type StreamReadiness =
  /** A producer frame was accepted or consumed by the provider. Deltas can be written. */
  | "ready"
  /** The socket or provider failed terminally before any frame was taken. */
  | "stream_failed"
  /**
   * The deployment opens no realtime lane at all: neither the merge nor the claim-delta
   * transport is on, so there is nothing to be ready on (#170-D).
   */
  | "stream_disabled"
  /** `?probe_manual=1`: no socket was opened automatically, so none was admitted. */
  | "manual"
  /** Stop won the race. Nobody is recording for the producer any more. */
  | "recording_stopped"
  /** The deadline expired with no producer admitted. */
  | "readiness_timeout";

/**
 * Producer readiness, owned here rather than in the claim-delta path (#170-C).
 *
 * [V] The transport gate of 2026-08-26 failed because the claim path *inferred* readiness
 * by asking `GET /claim-deltas/context` on a timer and believing the first `409
 * producer_not_accepted` it got back. That is a poll standing in for a signal, and it was
 * wrong twice over: the poll races the socket handshake it knows nothing about, and its
 * answer is a second-hand shadow of a fact this module holds first-hand. Admission landed
 * at ~8.5 s against a ~0.6 s budget and the recording streamed nothing.
 *
 * So readiness is a latch, settled by the event that actually means it: the gateway's
 * first `ack` for a producer frame the provider accepted or consumed. Everything else
 * that can end the question -- a dead socket, a capability that is off, manual mode, stop,
 * the deadline -- settles it too, so the claim path never has to guess from silence.
 *
 * Keyed by the recording generation. A latch that answered for the previous recording
 * must not answer for this one, and `resetForRecording` is what guarantees that: it is
 * called from `resetStreamForNewRecording`, on the same line of `startRecording` that
 * invalidates every other piece of per-recording state.
 */
const readiness: {
  generation: number;
  outcome: StreamReadiness | null;
  timer: ReturnType<typeof setTimeout> | null;
} = { generation: -1, outcome: null, timer: null };

/**
 * Whether work started for one recording may still touch anything shared (#170-C).
 *
 * The single liveness question, asked in one place, because four conditions have to hold
 * and no subset of them is sufficient:
 *
 * - **Generation**, for a recording that was superseded by a later one.
 * - **Session**, for the case a generation check structurally cannot see: a rejoin that
 *   replaces the session without the engine re-arming the latch leaves the generation
 *   matching and the session different.
 * - **A non-terminal readiness outcome**, which is the condition the first two miss
 *   entirely. [V] A capabilities request still in flight when the deadline fires -- or
 *   when the recording stops -- carries the *same* generation and the *same* session, so
 *   a guard built only on those two lets the continuation through: it patches
 *   `streamPromotion`, emits `stream_promotion`, and opens a fresh socket into a lane
 *   that has already been given up on and torn down.
 * - **An active recording**, and it is not redundant with the readiness check even
 *   though the wrap-up settles `recording_stopped`. The two are set at different
 *   moments: `finishSession` stops the stream several steps before it patches
 *   `recording: false`, so between them a continuation sees a stopped lane, and after
 *   them it sees no recording at all. Either alone leaves a window.
 *
 * Cancellation is therefore covered twice over, deliberately: this runs on continuations
 * that resumed seconds after the thing they were started for stopped existing.
 */
function isCurrentRecording(generation: number, sessionId: string): boolean {
  if (readiness.generation !== generation) return false;
  const app = useApp.getState();
  if (app.session?.session_id !== sessionId) return false;
  if (!app.recording) return false;
  if (readiness.outcome !== null && READINESS_ENDED.has(readiness.outcome)) return false;
  return true;
}

/**
 * Readiness outcomes after which this recording's stream is over.
 *
 * `ready` is deliberately absent: it is the beginning of a working stream, not the end of
 * one, and turns keep arriving after it.
 *
 * [V] `readiness_timeout` and `stream_failed` were here too, so the latch's answer to the
 * claim path also ended the lane: a stream that could not start, or ran out of reconnect
 * attempts, stopped trying for the rest of the recording. #356 separates the two. Audio
 * is no longer only useful live -- the archive needs every sample -- so those outcomes
 * still answer the claim path, but the lane keeps retrying slowly (see
 * `scheduleSlowRetry`). A lane now ends only with the recording, when the deployment has
 * no stream at all, or on a typed refusal (`stream.laneRefused`).
 */
const READINESS_ENDED: ReadonlySet<StreamReadiness> = new Set<StreamReadiness>([
  "recording_stopped",
  "stream_disabled",
]);

/**
 * Whether an uplink callback may still touch anything shared (#170-C).
 *
 * Four conditions, and each one closes a hole the others do not:
 *
 * - **Session and generation**, via `isCurrentRecording`, for a recording that has been
 *   superseded or replaced under the same generation.
 * - **Uplink identity**, because one recording can build a second uplink -- a stop that is
 *   still closing while a start opens -- and the loser's callbacks must not write over
 *   the winner's state or, worse, stop the winner's socket.
 * - **A non-terminal latch**, because the deadline and the wrap-up both tear a socket
 *   down deliberately, and the `onClosed` that follows is the sound of that teardown
 *   working. Treating it as news would overwrite the note the teardown just wrote.
 *
 * Applied to `onHealth`, `onMessage`, `onError` and `onClosed` alike: telemetry, the
 * provisional transcript, the state line and the readiness latch are all shared, and a
 * late callback that writes any of them is writing into somebody else's recording.
 */
function isLiveCallback(generation: number, sessionId: string, uplink: AudioUplink): boolean {
  if (!isCurrentRecording(generation, sessionId)) return false;
  // The one condition a callback adds over a continuation: one recording can hold two
  // uplinks when a stop is still closing as a start opens, and the loser must not write
  // over the winner's state or, worse, stop the winner's socket.
  return stream.uplink === uplink || stream.starting === uplink;
}

function clearReadinessTimer(): void {
  if (readiness.timer !== null) {
    clearTimeout(readiness.timer);
    readiness.timer = null;
  }
}

/**
 * Arm the latch for a new recording, and start its single deadline.
 *
 * The timer is armed here, at the top of the recording, rather than when somebody first
 * asks. Asking is what the old design did -- and a deadline measured from the first
 * question rather than from the start of the recording drifts with whatever else the
 * tick was doing.
 */
export function resetStreamReadiness(generation: number): void {
  clearReadinessTimer();
  readiness.generation = generation;
  readiness.outcome = null;
  readiness.timer = setTimeout(() => {
    // Guarded on the generation the timer was armed for: a recording that ended and was
    // replaced must not have its successor's question answered by the old clock.
    if (readiness.generation === generation && readiness.outcome === null) {
      readiness.outcome = "readiness_timeout";
      // And the socket goes with the verdict (#170-C). Fifteen seconds without a single
      // accepted producer frame is a socket that is not carrying anything, and leaving it
      // open costs the device a socket, a 100 ms flush timer and the frames that timer
      // keeps pushing at a gateway that is not admitting them.
      //
      // [V] This used to be a `stopStream`, which ended the lane for the rest of the
      // recording. #356: the socket still goes, but the lane does not -- a slow retry
      // takes its place, because this device's audio is still owed to the archive. A
      // reconnect already scheduled is left alone; it is the retry.
      const sessionId = useApp.getState().session?.session_id ?? "";
      const stale = stream.uplink ?? stream.starting;
      if (stale && isCurrentRecording(generation, sessionId) && !stream.clientStopRequested) {
        stream.uplink = null;
        stream.starting = null;
        stream.telemetry.stop(
          performance.now(),
          "readiness_timeout",
          { readiness: "timeout" },
          stream.mode,
          stream.lastAttempt,
        );
        telemetry.flush();
        stream.pendingFirstTurn = null;
        void stale.stop().catch(() => {
          /* Teardown is best effort; the latch has already answered. */
        });
        const retrying = scheduleSlowRetry(generation, sessionId, stream.mode, stream.lastAttempt);
        setUplinkState(retrying ? "starting" : "ended");
      }
    }
    readiness.timer = null;
  }, STREAM_READINESS_DEADLINE_MS);
}

/**
 * Settle the latch, if it belongs to this recording and has not settled already.
 *
 * First answer wins. The ack path and the failure path can both fire for one socket --
 * a stream that carried frames and then died is `ready`, not `stream_failed`, because
 * the producer *was* admitted and the claim path's question is only ever about that.
 */
export function settleStreamReadiness(generation: number, outcome: StreamReadiness): void {
  if (readiness.generation !== generation) return;
  if (readiness.outcome !== null) return;
  readiness.outcome = outcome;
  clearReadinessTimer();
}

/**
 * What the latch knows, or `null` while the question is still open.
 *
 * Read rather than awaited, and that is deliberate: the caller is the 500 ms scoring
 * tick, which must not block behind a fifteen-second answer. A `null` here costs the
 * tick nothing and no network request -- which is the whole difference from the poll
 * this replaces.
 */
export function streamReadiness(generation: number): StreamReadiness | null {
  return readiness.generation === generation ? readiness.outcome : null;
}

function patch(next: Parameters<ReturnType<typeof useApp.getState>["setSessionState"]>[0]): void {
  useApp.getState().setSessionState(next);
}

/**
 * The uplink's state, which the meeting screen reads for its upload health line. The
 * screen says what a state means in its own copy (`session.upload_*`), so no sentence
 * is kept here (#619).
 */
function setUplinkState(state: "off" | "starting" | "live" | "ended"): void {
  patch({ streamUplinkState: state });
}

/**
 * This device's upload against its capture, for the recording screen's health line.
 *
 * `acked` only moves forward: an ack replayed after a reconnect can carry an older
 * cursor, and upload health that briefly went backwards would read as lost audio.
 */
function noteUplinkHealth(captured: number, acked: number | null): void {
  const previous = useApp.getState().uplinkHealth;
  patch({
    uplinkHealth: {
      captured: Math.max(captured, previous?.captured ?? 0),
      acked: Math.max(acked ?? 0, previous?.acked ?? 0),
      sampleRate: K.SAMPLE_RATE,
    },
  });
}

/**
 * Flatten socket diagnostics into telemetry fields.
 *
 * Moved here verbatim from the diagnostics route along with the rest of the
 * lifecycle. The field names are the telemetry contract -- a series already holds
 * rows under them -- so they are copied exactly rather than tidied.
 */
export function socketTelemetryDetail(
  diagnostics: AudioUplinkSocketDiagnostics | undefined,
): Record<string, string | number | boolean | null> {
  if (!diagnostics) return {};
  return {
    socket_event: diagnostics.event,
    close_code: diagnostics.closeCode,
    close_reason: diagnostics.closeReason,
    socket_ready_state: diagnostics.readyState,
    socket_buffered_bytes: diagnostics.bufferedAmount,
    browser_online: diagnostics.browserOnline,
    visibility_state: diagnostics.visibilityState,
    was_clean: diagnostics.wasClean,
  };
}

function consumePendingFirstTurn(): PendingFirstTurn | null {
  const pending = stream.pendingFirstTurn;
  stream.pendingFirstTurn = null;
  return pending;
}

/** True while this device has a live or opening stream. Read-only, for views. */
export function streamUplinkActive(): boolean {
  return stream.uplink !== null || stream.starting !== null;
}

/**
 * The session reference device the gateway last named, or `null`.
 *
 * A getter rather than store state because its only reader is the parity artifact
 * export, which reads it inside a click handler -- nothing renders it, so putting it
 * in the store would re-render every subscriber for a value nobody displays.
 */
export function streamAnchorReferenceDeviceId(): string | null {
  return stream.anchorReferenceDeviceId;
}

/**
 * Open the realtime stream for the running recording.
 *
 * The recorder is passed in rather than read back out of `session.ts`, and that is
 * what keeps the dependency one-directional: the engine's wrap-up calls *into* this
 * module, so this module importing `session.ts` for a recorder would close the loop.
 * It also states the important property in the signature -- the stream borrows the
 * live recording and **never opens a second capture**, which is what makes it a
 * diagnostic on the real audio rather than a parallel one competing for the
 * microphone.
 *
 * No-ops unless a session is recording and no stream is already up, so it is safe to
 * call from a button, from the recording lifecycle, or from both.
 */
export async function startStream(
  recorder: Pick<AudioUplinkCapture, "sampleCount" | "seconds" | "sliceSamples" | "slice"> | null,
  mode: "auto" | "manual" = "auto",
  attempt = 1,
  recordingStartedAtMs?: number,
  // Defaulted to whichever recording the latch is currently armed for, so the
  // diagnostics button and the existing tests need no argument. The recording lifecycle
  // passes its own, which is what makes a callback from a superseded uplink inert.
  generation: number = readiness.generation,
): Promise<void> {
  const app = useApp.getState();
  const session = app.session;
  if (!session || !app.recording || streamUplinkActive()) return;
  // The recording this uplink will belong to, fixed here and re-checked at every seam
  // below. An uplink that opened for one session and settled another's latch is the
  // failure this pair of values exists to make impossible.
  const sessionId = session.session_id;
  if (!isCurrentRecording(generation, sessionId)) return;
  if (!recorder) {
    // Said before the settle, for the same reason the catch below reads liveness first.
    setUplinkState("off");
    // No capture, so no frame will ever be sent and no producer admitted. Answering now
    // rather than letting the deadline answer costs the claim path fifteen seconds of
    // waiting for something that cannot arrive.
    settleStreamReadiness(generation, "stream_failed");
    return;
  }

  stream.recorder = recorder;
  stream.mode = mode;
  stream.lastAttempt = attempt;
  stream.clientStopRequested = false;
  if (recordingStartedAtMs !== undefined) {
    stream.recordingStartedAtMs = recordingStartedAtMs;
  } else if (stream.recordingStartedAtMs === null) {
    stream.recordingStartedAtMs = performance.now();
  }
  attachNetworkListeners();

  // The mint budget (#943). The recording's own first connection is the host's mint and is
  // free, exactly as the core's `Uplink::start` opens with the ticket the host handed it;
  // every reconnect spends a token, and before Stop it leaves one for the drain. With
  // fewer than the two it needs, the lane waits on the bucket, never on the server.
  let spentMint = false;
  if (!stream.hostMintSpent) {
    stream.hostMintSpent = true;
  } else if (isOfflineNow()) {
    // Checked before the mint, never after one: a request that is issued keeps its token
    // even if the browser goes offline while it is in flight.
    armOfflineRetry(generation, sessionId, mode, attempt);
    return;
  } else if (tryConsumeMintToken(MINT_RESERVE_FOR_DRAIN)) {
    spentMint = true;
  } else {
    armMintWait(generation, sessionId, mode, attempt);
    return;
  }

  stream.timelineEpoch ??=
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

  const uplink = new AudioUplink({
    sessionId: session.session_id,
    capture: recorderCapture(recorder),
    timelineEpoch: stream.timelineEpoch ?? undefined,
    mode,
    attempt,
    recordingStartedAtMs: stream.recordingStartedAtMs ?? undefined,
    onHealth: (snapshot: AudioUplinkHealthSnapshot) => {
      // Guarded like every other callback: `stream_health` is attributed to whichever
      // session telemetry is bound to when it is logged, so a superseded uplink's
      // heartbeat would be filed against the recording that replaced it.
      if (!isLiveCallback(generation, sessionId, uplink)) return;
      const bufferedSamples = Math.max(
        0,
        snapshot.available_sample_count - snapshot.sent_sample_offset,
      );
      stream.telemetry.noteBufferedFrameHighWater(
        Math.ceil(bufferedSamples / STREAM_FRAME_SAMPLES),
      );
      telemetry.log("stream_health", snapshot);
      noteUplinkHealth(snapshot.available_sample_count, null);
    },
    onMessage: (message) => {
      // A superseded or finished uplink's gateway traffic must not reach the live
      // recording's state: its turns would land in another session's transcript and its
      // acks would answer a readiness question that is not its own -- including, after
      // the deadline has already given up, reviving a lane that was torn down.
      if (!isLiveCallback(generation, sessionId, uplink)) return;
      if (message.type === "ack") {
        setUplinkState("live");
        // Progress, so the next resumable close starts the resumable lane's backoff over.
        if (message.status === "accepted") stream.resumableReconnectAttempts = 0;
        // A connection that settles gives the fast lane and the mint accounting back (#943).
        // Without this, every drop after the first went straight to the 30 s slow retry,
        // and a flapping network read as unproductive however well it recovered. A timeout
        // or a pending admission is not a settle, and neither is an ack for another
        // timeline.
        if (
          (message.status === "accepted" || message.status === "consumed") &&
          message.timeline_epoch === stream.timelineEpoch
        ) {
          stream.reconnectAttempts = 0;
          stream.unproductiveMints = 0;
        }
        if (typeof message.next_sample_offset === "number") {
          noteUplinkHealth(stream.recorder?.sampleCount ?? 0, message.next_sample_offset);
        }
        // Readiness, first-hand (#170-C). `accepted` means the provider took the bytes;
        // `consumed` is the replay path re-feeding them after a reconnect. Both prove an
        // admitted producer, which is the one fact the claim-delta path needs and the
        // one it used to infer by polling a route that knows it second-hand.
        //
        // Pinned to this recording's timeline epoch as well as its generation: a
        // `consumed` ack can describe a cursor established under an earlier transport,
        // and readiness must not be settled by a producer belonging to a different
        // timeline than the one this recording is signing for.
        if (
          (message.status === "accepted" || message.status === "consumed") &&
          message.timeline_epoch === stream.timelineEpoch
        ) {
          settleStreamReadiness(generation, "ready");
        }
      } else if (message.type === "session_state") {
        // Advisory (#272 L4b). The SSE `state_change` and the 3 s state poll
        // carry the same fact; this arrives first because the audio socket is
        // the one transport a recording device is certainly holding open. The
        // handler is registered by `session.ts` rather than imported, to keep
        // this module from depending on the engine that owns it.
        const state = String(message.state ?? "");
        telemetry.log("stream_session_state", { state });
        setUplinkState("live");
        // `onSessionState` already ignores this once `stopping.busy` (#116), so
        // a frame that arrives after this device pressed Stop cannot start a
        // second wrap-up.
        sessionStateHandler?.({ state });
      } else if (message.event) {
        setUplinkState("live");
      } else if (message.type === "close") {
        // [V] Said "Gateway closed cleanly (<reason>)" for every close, including the
        // per-connection cap, which is not clean and is followed by a reconnect (#410).
        // What the close means is `onClosed`'s to say; this only reports the fact.
        setUplinkState("live");
      } else if (message.type === "turn") {
        const turnOrder = message.turn_order;
        const textLength = typeof message.text === "string" ? message.text.length : 0;
        const detail = {
          turn_order: typeof turnOrder === "number" ? turnOrder : -1,
          end_of_turn: message.end_of_turn === true,
          text_length: textLength,
        };
        if (typeof turnOrder === "number" && Number.isInteger(turnOrder) && turnOrder >= 0) {
          telemetry.log("stream_turn", detail);
          if (typeof message.text === "string" && message.text.trim()) {
            const atMs = performance.now();
            if (!stream.telemetry.firstTurn(turnOrder, atMs) && stream.starting === uplink) {
              stream.pendingFirstTurn ??= { turnOrder, atMs };
            }
          }
        }
      }
    },
    onError: (_error, diagnostics) => {
      // Checked *before* settling, so a late error cannot answer a question the deadline
      // or the wrap-up has already closed, and cannot stop the socket of whatever
      // recording is live now.
      if (!isLiveCallback(generation, sessionId, uplink)) {
        // Still torn down -- an abandoned socket keeps its file descriptor and its flush
        // timer -- but silently: the state line belongs to whoever is recording now.
        void uplink.stop().catch(() => {});
        return;
      }
      const kind: CloseKind = classifyClose(diagnostics);
      if (kind === "terminal_refusal") {
        settleStreamReadiness(generation, "stream_failed");
        // A typed refusal repeats on every retry, so it ends the lane outright (#356). The
        // reason goes to telemetry in the detail below, not into the note.
        refuseLane();
        void stopStream(
          "error",
          socketTelemetryDetail(diagnostics),
          "ended",
          mode,
          attempt,
        );
      }
      // Abnormal and resumable close recovery is handled in onClosed for orderly
      // lifecycle sequencing.
    },
    onClosed: (diagnostics) => {
      if (!isLiveCallback(generation, sessionId, uplink)) {
        // Release the reference so a superseded uplink cannot be mistaken for the live
        // one, and leave every piece of shared state to the recording that owns it. This
        // is also the ordinary path for a deliberate teardown: the deadline and the
        // wrap-up have already written their own note, and re-reporting the close as news
        // would replace it with a worse one.
        if (stream.uplink === uplink) stream.uplink = null;
        return;
      }

      const kind: CloseKind = classifyClose(diagnostics);
      const isDeliberate =
        stream.clientStopRequested || useApp.getState().streamUplinkState === "off";

      if (isDeliberate || kind === "clean") {
        stream.telemetry.stop(
          performance.now(),
          "closed",
          socketTelemetryDetail(diagnostics),
          mode,
          attempt,
        );
        telemetry.flush();
        stream.pendingFirstTurn = null;
        if (stream.uplink === uplink) {
          stream.uplink = null;
          // Only when the socket died under us. An orderly `stopStream` has already
          // written its own note, and overwriting it here would replace "stopped" with
          // "ended" on the one path where nothing went wrong.
          if (useApp.getState().streamUplinkState !== "off") setUplinkState("ended");
        }
        return;
      }

      if (kind === "resumable") {
        // The server closed this connection and kept its place (#410): the per-connection
        // cap, most often. Reconnect and resume from the durable boundary -- the next
        // uplink's frame-0 probe jumps straight to it, so nothing is lost and little is
        // re-sent. Not a refusal (no `laneRefused`, no `stream_failed`), and not a drop:
        // neither of the other lanes' budgets is spent, and this lane never runs out.
        stream.telemetry.stop(
          performance.now(),
          "reconnect",
          socketTelemetryDetail(diagnostics),
          mode,
          attempt,
        );
        telemetry.flush();
        stream.pendingFirstTurn = null;
        if (stream.uplink === uplink) stream.uplink = null;
        if (
          !isCurrentRecording(generation, sessionId) ||
          stream.clientStopRequested ||
          stream.recorder === null
        ) {
          // Stop got here first; its backlog upload finishes what this connection did not.
          return;
        }
        stream.resumableReconnectAttempts += 1;
        const resumeAttempt = stream.resumableReconnectAttempts;
        const backoffs = STREAM_RESUMABLE_RECONNECT_BACKOFF_MS;
        const backoffMs = backoffs[Math.min(resumeAttempt, backoffs.length) - 1];
        const wait = spacedWait("resumable", backoffMs);
        const closedAtMs = performance.now();
        setUplinkState("starting");
        armReconnectTimer(wait.lane, wait.ms, () => {
          if (!isCurrentRecording(generation, sessionId) || stream.clientStopRequested) return;
          const now = performance.now();
          stream.telemetry.reconnect(now, resumeAttempt, {
            close_code: diagnostics?.closeCode ?? null,
            close_reason: diagnostics?.closeReason ?? null,
            timeline_epoch: stream.timelineEpoch,
            gap_ms: Math.max(0, Math.round(now - closedAtMs)),
            lane: "resumable",
          });
          void startStream(
            stream.recorder,
            mode,
            attempt + 1,
            stream.recordingStartedAtMs ?? undefined,
            generation,
          );
        });
        return;
      }

      if (kind === "terminal_refusal") {
        settleStreamReadiness(generation, "stream_failed");
        refuseLane();
        stream.telemetry.stop(
          performance.now(),
          "error",
          socketTelemetryDetail(diagnostics),
          mode,
          attempt,
        );
        telemetry.flush();
        stream.pendingFirstTurn = null;
        if (stream.uplink === uplink) stream.uplink = null;
        // The reason is in the `stream_stop` detail above.
        setUplinkState("ended");
        return;
      }

      // A recoverable close. Which lane it recovers on is the whole of #264 S2: a
      // deliberate server restart, and anything that follows one closely enough to be
      // its fallout, gets minutes of patience; everything else keeps the seconds it had.
      const closedAtMs = performance.now();
      if (kind === "service_restart") {
        // Extended, not merely set: a redeploy that lands mid-window (or a rolling
        // replacement that cuts this device twice) is more restart, not less. The budget
        // deliberately does *not* reset with it -- three attempts is three attempts, or
        // "fail closed" would mean nothing in a deploy loop.
        stream.restartWindowUntilMs = closedAtMs + STREAM_RESTART_WINDOW_MS;
      }
      const lane: StreamProbeReconnectLane =
        closedAtMs < stream.restartWindowUntilMs ? "restart" : "abnormal";
      const onRestartLane = lane === "restart";

      const laneMax = onRestartLane
        ? MAX_STREAM_RESTART_RECONNECT_ATTEMPTS
        : MAX_STREAM_RECONNECT_ATTEMPTS;
      const laneAttempts = onRestartLane
        ? stream.restartReconnectAttempts
        : stream.reconnectAttempts;
      const nextAttempt = laneAttempts + 1;
      const canReconnect =
        isCurrentRecording(generation, sessionId) &&
        !stream.clientStopRequested &&
        nextAttempt <= laneMax &&
        stream.recorder !== null;

      if (!canReconnect) {
        settleStreamReadiness(generation, "stream_failed");
        stream.telemetry.stop(
          performance.now(),
          // The reason S3 reserved for exactly this outcome. Kept off the abnormal lane's
          // exhaustion path, which has reported "closed" since #170 and whose existing
          // telemetry series should not change shape for a slice that did not change it.
          onRestartLane ? "reconnect_failed" : "closed",
          socketTelemetryDetail(diagnostics),
          mode,
          attempt,
        );
        telemetry.flush();
        stream.pendingFirstTurn = null;
        if (stream.uplink === uplink) stream.uplink = null;
        // [V] A spent budget used to end the lane here. #356: the fast lane is spent,
        // but a recording that is still going keeps retrying slowly, because its audio
        // is still owed to the archive. Not retrying here means the recording is over,
        // and Stop's backlog upload has the rest.
        scheduleSlowRetry(generation, sessionId, mode, attempt);
        setUplinkState("ended");
        return;
      }

      // Perform bounded reconnect with backoff, against this lane's budget alone.
      if (onRestartLane) {
        stream.restartReconnectAttempts = nextAttempt;
      } else {
        stream.reconnectAttempts = nextAttempt;
      }
      stream.telemetry.stop(
        performance.now(),
        "reconnect",
        socketTelemetryDetail(diagnostics),
        mode,
        attempt,
      );
      telemetry.flush();
      if (stream.uplink === uplink) stream.uplink = null;

      const baseBackoffMs = onRestartLane
        ? STREAM_RESTART_RECONNECT_BACKOFF_MS[nextAttempt - 1] ?? 60_000
        : STREAM_RECONNECT_BACKOFF_MS[nextAttempt - 1] ?? 1_000;
      // Jitter on the restart lane's first attempt only -- see the constant's note. Read
      // once, here, so the delay actually slept and the one the state line quotes cannot
      // disagree.
      const backoffMs =
        onRestartLane && nextAttempt === 1
          ? baseBackoffMs + Math.floor(Math.random() * STREAM_RESTART_RECONNECT_JITTER_MS)
          : baseBackoffMs;
      const disconnectAtMs = closedAtMs;
      const wait = spacedWait(onRestartLane ? "restart" : "abnormal", backoffMs);

      setUplinkState("starting");

      armReconnectTimer(wait.lane, wait.ms, () => {
        if (!isCurrentRecording(generation, sessionId) || stream.clientStopRequested) {
          return;
        }

        const now = performance.now();
        const gapMs = Math.max(0, Math.round(now - disconnectAtMs));

        stream.telemetry.reconnect(now, nextAttempt, {
          close_code: diagnostics?.closeCode ?? null,
          close_reason: diagnostics?.closeReason ?? null,
          timeline_epoch: stream.timelineEpoch,
          gap_ms: gapMs,
          lane,
        });

        void startStream(
          stream.recorder,
          mode,
          // The uplink's own attempt number stays globally monotonic across both lanes --
          // `nextAttempt` is lane-local, and using it here would re-number a socket back
          // to 2 after a deploy.
          attempt + 1,
          stream.recordingStartedAtMs ?? undefined,
          generation,
        );
      });
    },
  });

  stream.uplink = uplink;
  stream.starting = uplink;
  stream.pendingFirstTurn = null;
  const startedAt = performance.now();
  setUplinkState("starting");
  try {
    await uplink.start();
    if (stream.uplink !== uplink || !isCurrentRecording(generation, sessionId)) {
      // Stopped, or superseded, while it was opening. The winner owns the state line --
      // and an uplink that finished opening into a recording that no longer exists is a
      // socket streaming one session's audio under another's identity.
      await uplink.stop().catch(() => {});
      return;
    }
    stream.telemetry.start(startedAt, mode, attempt);
    const firstTurn = consumePendingFirstTurn();
    if (firstTurn !== null) stream.telemetry.firstTurn(firstTurn.turnOrder, firstTurn.atMs);
    setUplinkState("live");
  } catch (error) {
    // Read before the settle, because the settle is itself a terminal outcome and would
    // otherwise make this recording look stale to its own failure report.
    const live = isCurrentRecording(generation, sessionId);
    // Everything `AudioUplink.start()` can fail on lands here: a refused ticket request,
    // a ticket response with no ticket in it, a socket that will not open, and the first
    // flush. None of them will ever produce an accepted producer frame, so the claim path
    // is told now rather than left to discover it from the deadline fifteen seconds later
    // (#170-C).
    settleStreamReadiness(generation, "stream_failed");
    const owner = stream.uplink === uplink;
    if (owner) {
      stream.telemetry.reset();
      stream.pendingFirstTurn = null;
      stream.uplink = null;
    }
    if (!live) return;
    // The mint accounting for this attempt (#943), gated to this recording and this uplink.
    // The token was taken only after a mint was actually issued, so it stays spent whatever
    // happens next -- the server may have counted it, and a browser that goes offline here
    // does not take it back. But a late failure from a superseded recording or uplink must
    // not spend the new one's spacing budget. A 429 is the limit itself: space every later
    // mint until one settles.
    if (spentMint && owner) {
      stream.unproductiveMints += 1;
      if ((error as { status?: unknown }).status === 429) {
        stream.unproductiveMints = Math.max(stream.unproductiveMints, MAX_UNPRODUCTIVE_MINTS);
      }
    }
    // The deployment stopped taking audio after this recording began (#388). That is a
    // decision, not an outage: asking again would only be refused again.
    if (owner && isAudioNotAccepted(error)) {
      refuseLane();
      setUplinkState("ended");
      return;
    }
    // [V] #356: a start that failed while this recording still owns the lane -- in an
    // outage, most often the ticket request itself, which opens no socket and so never
    // reaches `onClosed` -- retries slowly instead of ending it. Only the owner does: a
    // socket that closed before opening has already been handled by `onClosed`.
    const retrying = owner && scheduleSlowRetry(generation, sessionId, mode, attempt);
    setUplinkState(retrying ? "starting" : "ended");
  } finally {
    if (stream.starting === uplink) stream.starting = null;
  }
}

/**
 * Close the stream.
 *
 * `finalState` is `"off"` for a deliberate stop and `"ended"` for one forced on us,
 * because the two read differently to somebody watching: one is a thing they did,
 * the other is a thing that happened to them and will not recover this session.
 */
export async function stopStream(
  reason: AudioUplinkStopReason,
  detail: Record<string, string | number | boolean | null> = {},
  finalState: "off" | "ended" = "off",
  mode: "auto" | "manual" = "auto",
  attempt = 1,
): Promise<void> {
  stream.clientStopRequested = true;
  clearReconnectTimer();
  const uplink = stream.uplink ?? stream.starting;
  if (!uplink && useApp.getState().streamUplinkState === "off") return;
  stream.telemetry.stop(performance.now(), reason, detail, mode, attempt);
  telemetry.flush();
  stream.pendingFirstTurn = null;
  // Cleared before the await, so `onClosed` cannot mistake this for a socket that
  // died on its own and overwrite the note below. Stashed in the same breath,
  // because `awaitStreamClosed` is the other half of this stop and the reference
  // being cleared here is the only handle on the socket it has to wait for
  // (#272 R1 -- without the stash it awaited `null` and returned immediately).
  stream.closingUplink = uplink ?? null;
  if (stream.uplink === uplink) stream.uplink = null;
  if (stream.starting === uplink) stream.starting = null;
  setUplinkState(finalState);
  stream.stopRequestedAtMs = Date.now();
  if (uplink) await uplink.stop();
}

/**
 * Await the gateway's close for the uplink that was stopped, bounded by the uplink.
 *
 * Separated from `stopStream` so the caller chooses *when* to pay for it (#272
 * L1). `stopStream` now returns as soon as the close frame is away; this is the
 * other half, and `finishSession` puts it after the report block instead of in
 * front of final scoring and the watermark POST.
 *
 * Resolves immediately when there is no uplink, and never rejects: every caller
 * is on a teardown path where a socket that would not close politely must not
 * become an error.
 */
export async function awaitStreamClosed(): Promise<void> {
  await awaitGatewayClose();
  // And the post-stop backlog upload, if Stop found audio still owed (#356). Bounded
  // by `STREAM_BACKLOG_UPLOAD_WINDOW_MS`, and it never rejects.
  const backlog = stream.backlogUpload;
  if (backlog) {
    await backlog;
    if (stream.backlogUpload === backlog) stream.backlogUpload = null;
  }
}

/**
 * Wait until the server has the audio this device still owed at Stop (#894).
 *
 * `finishSession` awaits this before this device's final watermark and its report. The
 * server refuses a stream ticket to a guest that has left and to everyone once every
 * report is in, and its closing sweep can move the meeting on once every final watermark
 * is in, so audio still owed after either could never be delivered. The apps hold both the
 * same way (#845).
 *
 * Resolves as soon as the server has acknowledged every captured sample -- on an ordinary
 * online stop, one round trip after the last frame, without waiting for the gateway's
 * close (#272 L1). A device that was offline at Stop waits for its backlog upload, which
 * is bounded by `STREAM_BACKLOG_UPLOAD_WINDOW_MS` from Stop. Resolves at once when nothing
 * is owed, and never rejects.
 */
export async function awaitAudioDelivered(): Promise<void> {
  const delivered = stream.backlogDelivered;
  if (delivered) await delivered;
}

async function awaitGatewayClose(): Promise<void> {
  // `closingUplink` first, and it is the one that matters: `stopStream` has
  // already nulled `stream.uplink` by the time any caller gets here, so reading
  // that alone made this function a no-op on every deliberate stop (#272 R1).
  // `stream.uplink` remains the fallback for the callers that reach a teardown
  // without a `stopStream` in front of them.
  const uplink = stream.closingUplink ?? stream.uplink;
  if (!uplink) return;
  const stoppedAt = stream.stopRequestedAtMs;
  await uplink.closed();
  // Cleared unconditionally once awaited, whether or not this call is the one
  // that logs: the socket is settled, and an uplink left here would be a stale
  // handle for the next recording's teardown to wait on.
  if (stream.closingUplink === uplink) stream.closingUplink = null;
  // Once per stop, not once per caller. `finishSession` awaits this after the
  // report *and* its outer `finally` reaches `teardown`, which awaits it again;
  // the second call resolves instantly but would still log a second row for the
  // same close, with a longer `dur` off the same origin. Clearing the origin is
  // the flag.
  if (stoppedAt === null) return;
  stream.stopRequestedAtMs = null;
  // `forced` separates "the gateway closed cleanly" from "CLOSE_WAIT_MS ran out
  // and we closed it ourselves". Both are normal; only the second means the
  // provider terminate or a drain outlasted the client's patience, which is the
  // thing worth being able to count across sessions.
  telemetry.log("stream_gateway_closed", { forced: uplink.closeWasForced() }, Date.now() - stoppedAt);
}

/**
 * Where `session.ts` hangs its `onSessionState`, so the `session_state` frame
 * can reach it without this module importing the engine that imports it.
 */
let sessionStateHandler: ((event: { state: string }) => void) | null = null;

export function setStreamSessionStateHandler(
  handler: ((event: { state: string }) => void) | null,
): void {
  sessionStateHandler = handler;
}

/**
 * The recording is ending, so the live view ends with it.
 *
 * Called by the engine before it stops the recorder, because the stream reads that
 * recorder's buffer: stopping in the other order would leave a socket flushing
 * frames out of a capture the session has finished with.
 */
export async function stopStreamForRecordingEnd(): Promise<void> {
  stream.clientStopRequested = true;
  clearReconnectTimer();
  // The meeting is over: the network listener goes with it, so a finished recording does
  // not keep a `window` handler (#943).
  detachNetworkListeners();
  // Before the early return below, and before any awaiting: the claim path must never
  // be left waiting on a producer for a recording that has ended, whether or not a
  // socket was ever opened.
  settleStreamReadiness(readiness.generation, "recording_stopped");
  // Taken before the fields are cleared (#356): the backlog upload below needs this
  // recording's capture and timeline, and the recorder's buffer stays readable after
  // `stop()` releases the microphone.
  const live = stream.uplink ?? stream.starting;
  const backlog: BacklogContext | null =
    stream.recorder !== null && stream.timelineEpoch !== null && !stream.laneRefused
      ? {
          generation: readiness.generation,
          sessionId: useApp.getState().session?.session_id ?? "",
          recorder: stream.recorder,
          timelineEpoch: stream.timelineEpoch,
          mode: stream.mode,
          attempt: stream.lastAttempt,
          recordingStartedAtMs: stream.recordingStartedAtMs,
          uplink: live,
          uplinkWasOpen: live !== null && stream.starting !== live,
        }
      : null;
  stream.timelineEpoch = null;
  stream.recorder = null;
  if (streamUplinkActive() || useApp.getState().streamUplinkState !== "off") {
    await stopStream("recording_stopped");
  }
  // [V] This used to sit behind an early return for a lane that was already "off" -- a
  // stream stopped by hand before Stop -- so that device's remaining audio was never sent.
  // After Stop the backlog is always uploaded, unless the server refused this recording
  // (`laneRefused`, above) (#410).
  if (backlog !== null && backlog.sessionId) {
    let delivered = () => {};
    stream.backlogDelivered = new Promise<void>((resolve) => {
      delivered = resolve;
    });
    // The window bounds the hold on its own clock. The upload checks it only between
    // attempts, and an attempt on a black-holed network -- a ticket request or a socket
    // that neither opens nor fails -- can outlast it.
    const windowTimer = setTimeout(delivered, STREAM_BACKLOG_UPLOAD_WINDOW_MS);
    stream.backlogUpload = uploadBacklogAfterStop(backlog, delivered)
      .catch(() => {
        /* Best effort, and it reports itself; a teardown must not see it throw. */
      })
      // However the upload ends, the report it was holding goes.
      .finally(() => {
        clearTimeout(windowTimer);
        delivered();
      });
  }
}

/**
 * How long after Stop this device keeps trying to upload audio it still owes.
 *
 * [D] 90 s, like the uplink's own stop drain and for the same reason: inside the
 * server's 120 s stop barrier, with room for the close and the archive drain after it.
 */
export const STREAM_BACKLOG_UPLOAD_WINDOW_MS = 90_000;
/** Waits between backlog reconnects. Short, because the window above is the bound. */
const STREAM_BACKLOG_RETRY_MS = [1_000, 2_000, 4_000, 8_000] as const;

interface BacklogContext {
  generation: number;
  sessionId: string;
  recorder: Pick<AudioUplinkCapture, "sampleCount" | "seconds" | "sliceSamples" | "slice">;
  timelineEpoch: string;
  mode: "auto" | "manual";
  attempt: number;
  recordingStartedAtMs: number | null;
  uplink: AudioUplink | null;
  uplinkWasOpen: boolean;
}

/**
 * Finish sending this recording's audio after Stop, reopening the socket if need be (#356).
 *
 * The live uplink's own `stop()` drains what it can. This covers everything else: a device
 * that was disconnected at Stop, waiting on a retry, still opening, or whose drain was cut
 * off. Each connection sends one probe frame, jumps to the server's durable cursor and
 * sends the rest, so a reconnect costs only the tail the server does not have.
 *
 * Detached from the stop path, and awaited by `awaitStreamClosed`. `delivered` is called
 * once the server has the audio, before the gateway's close, and is what `finishSession`
 * holds this device's final watermark and report on (#894); the caller also calls it
 * however this ends. Cancelled by a new recording, through the generation.
 */
async function uploadBacklogAfterStop(ctx: BacklogContext, delivered: () => void): Promise<void> {
  const deadline = performance.now() + STREAM_BACKLOG_UPLOAD_WINDOW_MS;
  const current = () => readiness.generation === ctx.generation && !stream.laneRefused;
  // The live connection, already stopped. Its close is `awaitGatewayClose`'s to collect.
  if (ctx.uplink !== null && ctx.uplinkWasOpen && (await ctx.uplink.whenDelivered())) return;
  let attempt = ctx.attempt;
  for (let tries = 0; current(); tries += 1) {
    const remaining = deadline - performance.now();
    if (remaining <= 0) break;
    // Offline before the mint, so no request is issued and no token is spent; the drain
    // just waits. A request that is issued keeps its token even if the browser goes
    // offline while it is in flight (#943).
    if (isOfflineNow()) {
      await new Promise<void>((resolve) =>
        setTimeout(resolve, Math.min(STREAM_OFFLINE_RETRY_MS, Math.max(0, deadline - performance.now()))),
      );
      continue;
    }
    // The drain's own reserve (#943): a pre-Stop reconnect always left one token, so a Stop
    // at the end of an outage still gets a connection. It spends a token like any other
    // mint, and waits for a refill if an earlier attempt used the last one.
    if (!tryConsumeMintToken(1)) {
      const refillWaitMs = Math.min(mintWaitMs(1), Math.max(0, deadline - performance.now()));
      if (refillWaitMs <= 0) break;
      await new Promise<void>((resolve) => setTimeout(resolve, refillWaitMs));
      continue;
    }
    attempt += 1;
    const uplink = new AudioUplink({
      sessionId: ctx.sessionId,
      capture: recorderCapture(ctx.recorder),
      timelineEpoch: ctx.timelineEpoch,
      mode: ctx.mode,
      attempt,
      recordingStartedAtMs: ctx.recordingStartedAtMs ?? undefined,
      stopDrainDeadlineMs: remaining,
    });
    let opened = false;
    try {
      await uplink.start();
      opened = true;
    } catch (error) {
      // The deployment takes no audio now (#388): final, so stop asking.
      if (isAudioNotAccepted(error)) return;
      // Any refused ticket or a socket that would not open: the token stays spent, and the
      // drain waits and tries again.
    }
    if (opened) {
      await uplink.stop();
      if (await uplink.whenDelivered()) {
        delivered();
        await uplink.closed();
        telemetry.log("stream_backlog_uploaded", { attempt, tries: tries + 1 });
        return;
      }
    }
    if (!current()) return;
    const waitMs = Math.min(
      STREAM_BACKLOG_RETRY_MS[tries] ?? STREAM_BACKLOG_RETRY_MS[STREAM_BACKLOG_RETRY_MS.length - 1],
      Math.max(0, deadline - performance.now()),
    );
    await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
  }
  if (current()) telemetry.log("stream_backlog_upload_failed", { attempt });
}

/**
 * Start the uplink for a recording that has just begun.
 *
 * Unconditional now (#358): the device's audio is the meeting's input, not a live
 * view, so there is no capability gate to ask first. The name is kept for its
 * callers; #356 owns the uplink's shape from here.
 *
 * Deliberately not awaited by `startRecording`: opening a socket must never be
 * something a recording waits on.
 */
export async function startStreamIfEnabled(
  recorder: Pick<AudioUplinkCapture, "sampleCount" | "seconds" | "sliceSamples" | "slice"> | null,
  mode: "auto" | "manual" = "auto",
  attempt = 1,
  recordingStartedAtMs?: number,
  generation: number = readiness.generation,
): Promise<void> {
  void startStream(recorder, mode, attempt, recordingStartedAtMs, generation);
}

/** A new recording is a new timeline; nothing carries over from the last one. */
export function resetStreamForNewRecording(generation: number): void {
  // Invalidate, clear, arm, then stop -- in that order, and each step is load-bearing.
  //
  // The previous recording's uplink is taken *first* and its references dropped, so that
  // by the time anything is torn down every one of its callbacks already fails the
  // identity check in `isLiveCallback` -- including the `onClosed` its own stop will
  // fire. Arming the new generation before the stop closes the same door a second way.
  //
  // Stopping it at all is the part that was missing (#170-C): a socket left open on the
  // previous recording keeps its descriptor and its 100 ms flush timer, and goes on
  // pushing that recording's frames at the gateway while this one records.
  clearReconnectTimer();
  const previous = stream.uplink ?? stream.starting;
  stream.uplink = null;
  stream.starting = null;
  // The previous recording's stop may never have been awaited -- a discard, an
  // unmount. Its socket is force-closed below either way, and a handle carried
  // into this recording would only give its teardown somebody else's socket to
  // wait on.
  stream.closingUplink = null;
  // The previous recording's backlog upload, if any, is cancelled by the generation
  // armed below; nothing here should wait on it.
  stream.backlogUpload = null;
  stream.backlogDelivered = null;
  stream.laneRefused = false;
  stream.lastAttempt = 1;
  stream.recorder = null;
  stream.timelineEpoch = null;
  stream.anchorReferenceDeviceId = null;
  stream.pendingFirstTurn = null;
  stream.reconnectAttempts = 0;
  // Both halves of the restart lane, for the same reason the line above exists: the
  // window is a claim about the *server*, but the budget is this recording's, and a new
  // recording that inherited a spent restart budget would fail closed on the first 1012
  // it ever saw.
  stream.restartWindowUntilMs = 0;
  stream.restartReconnectAttempts = 0;
  stream.resumableReconnectAttempts = 0;
  // The mint budget is this recording's too (#943): a new meeting gets the full bucket
  // and its own free first connection, and the last recording's refill timer does not
  // reach into it.
  stream.mintTokens = STREAM_MINT_BUCKET;
  clearMintRefillTimer();
  stream.unproductiveMints = 0;
  stream.hostMintSpent = false;
  detachNetworkListeners();
  stream.clientStopRequested = false;
  stream.recordingStartedAtMs = null;
  stream.telemetry.reset();
  resetStreamReadiness(generation);
  setUplinkState("off");
  // With the rest of the stream state: the last recording's upload is not this one's.
  patch({ uplinkHealth: null, uplinkRefused: false });
  // Exactly once: the references above are already cleared, so a second reset before the
  // next start finds nothing to stop.
  if (previous) void previous.stop().catch(() => {});
}
